import { useState } from 'react';
import mapService from '../../services/api/mapService';

export const useGraphData = (graphs, graphElements) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [graphsData, setGraphsData] = useState({});

  const fetchGraphsData = async () => {
    setLoading(true);
    setError(null);
    try {
      const requests = [];
      const visibleGraphs = graphs.filter((graph) => graph.status === 'visible');
      visibleGraphs.forEach((graph) => {
        if (!graph.dateDebut || !graph.dateFin || !graph.elements?.length) return;
        const production = [];
        const consumption = [];
        const comparison = [];
        graph.elements.forEach((elementId) => {
          const element = graphElements.find((item) => item.id === elementId);
          if (!element) return;
          const settings = graph.elementSettings[elementId] || { showConsommation: true, showProduction: true };
          const payload = { zoneId: element.id, zoneName: element.name, coordinates: element.coordonates, dateDebut: graph.dateDebut, dateFin: graph.dateFin, enablePrediction: graph.enablePrediction || false };
          if (settings.showProduction && settings.showConsommation) comparison.push(payload);
          else if (settings.showProduction) production.push(payload);
          else if (settings.showConsommation) consumption.push(payload);
        });
        const predictionEnabled = graph.enablePrediction || false;
        if (production.length) requests.push({ graph, type: 'production', payload: production, promise: mapService.getConsumptionByZone(production, 'production', predictionEnabled) });
        if (consumption.length) requests.push({ graph, type: 'consumption', payload: consumption, promise: mapService.getConsumptionByZone(consumption, 'consumption', predictionEnabled) });
        if (comparison.length) requests.push({ graph, type: 'comparaison', payload: comparison, promise: mapService.getConsumptionByZone(comparison, 'comparaison', predictionEnabled) });
      });
      if (!requests.length) { setError('No visible chart has a date range and active elements.'); setLoading(false); return; }
      const results = await Promise.allSettled(requests.map((request) => request.promise));
      const grouped = {};
      results.forEach((result, index) => {
        const request = requests[index];
        const { graph, type } = request;
        grouped[graph.id] ||= { graphId: graph.id, graphName: graph.name, production: null, consumption: null, comparaison: null, enablePrediction: graph.enablePrediction || false, errors: [] };
        if (result.status !== 'fulfilled') { grouped[graph.id].errors.push({ type, error: result.reason?.message || 'Unknown error' }); return; }
        const data = result.value.data;
        if (type === 'comparaison') {
          grouped[graph.id].production = data.filter((item) => item.type === 'production');
          grouped[graph.id].consumption = data.filter((item) => item.type === 'consumption');
          grouped[graph.id].comparaison = data;
        } else grouped[graph.id][type] = data;
      });
      setGraphsData(grouped);
      if (Object.values(grouped).some((item) => item.errors.length)) setError('Some requests failed. Check the browser console for details.');
    } catch (requestError) {
      setError(requestError.message || 'An error occurred while loading chart data.');
    } finally {
      setLoading(false);
    }
  };

  const clearGraphsData = () => { setGraphsData({}); setError(null); };
  const getGraphData = (graphId) => graphsData[graphId] || null;
  const hasGraphData = (graphId) => !!graphsData[graphId];
  const getLoadedGraphsCount = () => Object.keys(graphsData).length;
  const isPredictionEnabled = (graphId) => !!graphsData[graphId]?.enablePrediction;

  return { loading, error, graphsData, fetchGraphsData, clearGraphsData, getGraphData, hasGraphData, getLoadedGraphsCount, isPredictionEnabled };
};
